import { getToken } from './auth.service';

export const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

export function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const token = getToken();
  if (!token) {
    return fetch(input, init);
  }
  const headers = new Headers(init?.headers);
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}

/** Медиа и файловые ссылки уходят в <audio src>/<img src>/скачивания без заголовков —
 *  токен передаётся query-параметром access_token (поддержан backend'ом). */
export function withAccessToken(url: string): string {
  const token = getToken();
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}access_token=${encodeURIComponent(token)}`;
}

function parseAttachVideoError(status: number, detail: unknown): string {
  const msg = typeof detail === 'string'
    ? detail
    : Array.isArray(detail)
      ? detail.map((item) => {
        const row = item as { msg?: string };
        return row.msg || '';
      }).filter(Boolean).join('. ')
      : '';

  if (status === 404) {
    if (msg === 'Not Found') {
      return 'Сервис загрузки видео недоступен. Перезапустите backend: cd backend && ./venv/bin/python run.py';
    }
    return msg || 'Встреча не найдена';
  }
  if (status === 400 && msg) return msg;
  if (status === 413) return 'Файл слишком большой';
  return msg || 'Не удалось прикрепить видео';
}

export type SpeakerListItem = {
  id: number;
  name: string;
  position: string | null;
  created_at: string;
  has_voice: boolean;
  qdrant_point_id?: number | null;
  /** Владелец голосового профиля (задача SP1) — не null, если это чей-то «Мой голос». */
  user_id?: number | null;
};

/** GET/POST /api/speakers/my-voice (задача SP1): голосовой профиль текущего пользователя. */
export type MyVoiceStatus = {
  has_voice: boolean;
  speaker_id?: number;
  name?: string;
  created_at?: string;
};

export type MeetingListItem = {
  id: string;
  name: string;
  date: string;
  duration: string;
  status: string;
  is_favorite?: boolean;
  has_img?: boolean;
  created_at?: string;
};

/** Пользователь с доступом к встрече (задача P4, backend/app/api/meeting_shares.py). */
export type ShareUser = {
  id: number;
  email: string;
  full_name?: string | null;
};

/** Встреча, расшаренная текущему пользователю (GET /meetings/shared-with-me). */
export type SharedMeeting = {
  id: string;
  name: string;
  date: string;
  owner_id?: number | null;
  owner_email?: string | null;
};

// ===== Бот-сессии — подключение бота к встрече (задача F2, backend/app/api/bots.py) =====

export type BotPlatform = 'teams' | 'meet' | 'zoom';

/** scheduled -> joining -> recording -> uploading -> processing (запись принята, meeting_id
 *  проставлен, дальше обычный пайплайн обработки) / failed (см. error). "done"/"completed"
 *  bots.py сегодня никогда не проставляет (доводит статус только до "processing") — обрабатываем
 *  их тоже на UI, на случай если бэкенд начнёт слать финальный статус по завершении пайплайна. */
export type BotSessionStatus =
  | 'scheduled'
  | 'joining'
  | 'recording'
  | 'uploading'
  | 'processing'
  | 'done'
  | 'completed'
  | 'failed';

/** BotSessionResponse бэкенда (POST/GET /api/bots/sessions[, /{id}]). */
export type BotSessionResponse = {
  id: number;
  platform: BotPlatform;
  meeting_url: string;
  scheduled_at: string | null;
  status: BotSessionStatus;
  meeting_id: string | null;
  error: string | null;
  created_at: string | null;
};

export type BotSessionCreatePayload = {
  platform: BotPlatform;
  meeting_url: string;
  /** ISO datetime — только при выборе «К времени» в bot-connect-modal. Важно: сегодня
   *  bot_runner_service.dispatch() отправляет join сразу, независимо от scheduled_at
   *  (см. backend/app/services/bot_runner_service.py) — поле пока лишь сохраняется для истории. */
  scheduled_at?: string;
};

// ===== Задачи (задача C1, backend/app/api/tasks.py + tasks_from_meeting.py) =====

export type TaskStatus = 'todo' | 'in_progress' | 'review' | 'done';

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

/** TaskResponse бэкенда: meeting_id — встреча-источник (задаётся только через from-action-items). */
export type TaskItem = {
  id: number;
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  project?: string | null;
  assignee_id?: number | null;
  owner_id: number;
  meeting_id?: string | null;
  due_date?: string | null;
  estimated_hours?: number | null;
  actual_hours?: number | null;
  tags: string[];
  position: number;
  created_at: string;
  updated_at: string;
};

export type TaskListResult = {
  tasks: TaskItem[];
  total: number;
  page: number;
  limit: number;
};

export type TaskFilters = {
  project?: string;
  status?: TaskStatus;
  assignee_id?: number;
  meeting_id?: string;
  /** true — я владелец ИЛИ исполнитель; без флага — только мои как владельца. */
  mine?: boolean;
  page?: number;
  limit?: number;
};

export type TaskCreatePayload = {
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  project?: string;
  assignee_id?: number;
  /** ISO datetime, напр. "2026-07-15T00:00:00" */
  due_date?: string;
  tags?: string[];
};

export type TaskUpdatePayload = Partial<Omit<TaskCreatePayload, 'due_date' | 'assignee_id'>> & {
  /** null — очистить срок (PUT шлёт только переданные поля). */
  due_date?: string | null;
  assignee_id?: number | null;
  position?: number;
};

/** Один элемент POST /meetings/{id}/tasks/from-action-items — уже отредактированное
 *  пользователем поручение (не сырой action_item из LLM-конспекта). */
export type ActionItemTaskPayload = {
  title: string;
  description?: string;
  assignee_id?: number;
  /** "YYYY-MM-DD" */
  due_date?: string;
  priority?: TaskPriority;
};

export type SummaryStructured = {
  summary?: string;
  key_topics?: string[];
  decisions?: string[];
  action_items?: string[];
  questions?: string[];
};

export type ToneSegment = {
  start: number;
  end: number;
  tone: 'neutral' | 'constructive' | 'tense';
};

export type KeyMoment = {
  time: number;
  text: string;
  tag: string;
  tag_type: 'task' | 'dispute' | 'conflict' | 'decision' | 'action';
};

export type KeywordStat = {
  word: string;
  count: number;
};

export type ThemeKeyword = {
  word: string;
  count: number;
};

export type KeywordTheme = {
  label: string;
  icon: string;
  color: string;
  mention_count: number;
  insight: string;
  summary?: string;
  keywords: ThemeKeyword[];
};

export type ActionItem = {
  time: number;
  text: string;
  assignee?: string;
  deadline?: string;
};

export type SpeakerTone = {
  speaker: string;
  tone: 'neutral' | 'constructive' | 'tense';
  label: string;
  score?: number;
};

export type InsightsRefreshStatus = {
  meeting_id?: string;
  status: 'idle' | 'generating' | 'done' | 'error';
  message?: string;
  insights?: MeetingInsights;
  started_at?: string;
  finished_at?: string;
};

export type KpiRow = {
  metric: string;
  plan?: number | null;
  fact?: number | null;
  unit?: string;
  variance_pct?: number | null;
  timestamp?: string;
};

export type InsightDecision = {
  time: number;
  text: string;
  speaker?: string;
};

export type InsightConflict = {
  time: number;
  text: string;
  speakers?: string[];
  tag?: string;
  tag_type?: 'conflict' | 'dispute';
};

export type EngagementRow = {
  speaker: string;
  replica_count: number;
  initiative_count: number;
  avg_words: number;
  engagement_index: number;
};

export type AgreementIndex = {
  agreement_index: number;
  label: string;
  interrupt_score?: number;
  conflict_score?: number;
  top_interruption_pairs?: { from: string; to: string; count: number }[];
};

export type TaggedReplica = {
  timestamp: string;
  timestamp_sec: number;
  speaker: string;
  text: string;
  translated_text?: string | null;
  tags: string[];
};

export type SummaryCard = {
  id: string;
  label: string;
  value: string;
  detail?: string;
};

export type SlideTimelineEntry = {
  time_sec?: number;
  timestamp?: string;
  slide_index?: number;
  title?: string | null;
  bullets?: string[];
  content_type?: string;
  ocr_text?: string | null;
  is_scene_change?: boolean;
  error?: string;
  thumbnail_url?: string;
  speech_excerpt?: string | null;
  speech_segment_count?: number;
  keyword_overlap?: number;
  speech_alignment?: {
    aligned?: boolean | null;
    note?: string | null;
    method?: string;
    overlap?: number;
  };
  summary?: string | null;
  is_presentation?: boolean;
  thumbnail_crop_url?: string;
};

export type SlideMetricSummary = {
  label?: string;
  value?: string;
  context?: string;
};

export type SlideSummaryItem = {
  slide_index?: number;
  title?: string;
  summary?: string;
  key_points?: string[];
};

export type PresentationDeck = {
  deck_id?: number;
  title?: string;
  presenter?: string;
  start_sec?: number;
  end_sec?: number;
  start_time?: string;
  end_time?: string;
  slides_count?: number;
  slides?: SlideTimelineEntry[];
};

export type SlideDeckSummary = {
  deck_summary?: string;
  overview?: string;
  narrative?: string;
  key_topics?: string[];
  key_metrics?: SlideMetricSummary[];
  takeaways?: string[];
  slide_summaries?: SlideSummaryItem[];
  slides_count?: number;
  generated_at?: string;
};

export type SpeakerGenderInfo = {
  gender?: string;
  confidence?: number;
  display?: string;
};

export type VideoScreenInsightRow = Record<string, string | number | null>;

export type VideoScreenInsightSection = {
  title: string;
  source_file?: string;
  presenter?: string;
  timestamp?: string;
  unit?: string;
  columns?: string[];
  rows?: VideoScreenInsightRow[];
  notes?: string;
};

export type VideoScreenLeadershipMetric = {
  label: string;
  value: string;
  tone?: 'positive' | 'negative' | 'neutral';
};

export type VideoScreenLeadershipBriefSection = {
  id?: string;
  label: string;
  text: string;
  tone?: 'positive' | 'negative' | 'warning' | 'neutral' | 'success';
};

export type VideoScreenLeadershipBriefItem = {
  id?: string;
  label?: string;
  text: string;
  bullets?: string[];
  action?: string;
  tone?: 'positive' | 'negative' | 'warning' | 'neutral' | 'success';
};

export type VideoScreenLeadershipBriefBlock = {
  id?: string;
  number?: number;
  title: string;
  subtitle?: string;
  tone?: 'positive' | 'negative' | 'warning' | 'neutral' | 'success';
  items: VideoScreenLeadershipBriefItem[];
};

export type VideoScreenProtocolRisk = {
  id: string;
  title: string;
  description?: string;
  deviation_display?: string;
  assignee: string;
  deadline?: string;
  status_badge?: string;
  tags?: string[];
};

export type VideoScreenPendingAssignment = {
  meeting_at?: string;
  default_assignees?: string;
  note?: string;
  overrides?: { match?: string; assignees?: string }[];
};

export type VideoScreenLeadership = {
  brief?: string;
  brief_title?: string;
  brief_summary?: string;
  brief_sections?: VideoScreenLeadershipBriefSection[];
  brief_blocks?: VideoScreenLeadershipBriefBlock[];
  previous_meeting_note?: string;
  risk_hints?: { match?: string; section?: string; reason?: string }[];
  metrics?: VideoScreenLeadershipMetric[];
  protocol_risks?: VideoScreenProtocolRisk[];
  pending_assignments?: VideoScreenPendingAssignment;
};

export type VideoScreenInsights = {
  overview?: string;
  executive_summary?: string;
  leadership?: VideoScreenLeadership;
  source?: string;
  generated_at?: string;
  presenters?: string[];
  highlights?: string[];
  sections?: VideoScreenInsightSection[];
  glossary?: { term: string; definition: string }[];
};

export type VideoAnalysisMeta = {
  status?: string;
  frames_analyzed?: number;
  message?: string;
  content_stats?: {
    frames_total?: number;
    screen_share_frames?: number;
    speech_aligned?: number;
    speech_misaligned?: number;
    content_types?: Record<string, number>;
  };
};

export type MeetingInsights = {
  tone_summary: {
    overall: 'neutral' | 'constructive' | 'tense';
    label: string;
    tension_count: number;
  };
  tone_segments: ToneSegment[];
  key_topics: { time: number; topic: string }[];
  key_moments?: KeyMoment[];
  keywords?: KeywordStat[];
  themes?: KeywordTheme[];
  action_items?: ActionItem[];
  speaker_tones?: SpeakerTone[];
  kpi_table?: KpiRow[];
  decisions?: InsightDecision[];
  conflicts?: InsightConflict[];
  engagement_by_speaker?: EngagementRow[];
  agreement_index?: AgreementIndex;
  tagged_replicas?: TaggedReplica[];
  numbers_found?: Record<string, unknown>[];
  summary_cards?: SummaryCard[];
};

// ===== Задача F1/B1/B2/H1: разговорные метрики, слова-паразиты, эмоции, просодика =====
// (backend/app/services/conversation_metrics_service.py, segment_postprocess_service.py,
//  emotion_analysis_service.py, prosody_analysis_service.py — ключи словарей 1-в-1 с ответом API)

export type ConversationTalkRatioBySpeaker = {
  seconds: number;
  percent: number;
};

export type ConversationTalkRatio = {
  available: boolean;
  by_speaker: Record<string, ConversationTalkRatioBySpeaker>;
  gini: number;
  total_speech_seconds: number;
};

export type ConversationLongestMonologue = {
  speaker: string;
  duration_seconds: number;
  start_seconds: number;
};

export type ConversationTurns = {
  total: number;
  avg_length_seconds: number;
  longest_monologue: ConversationLongestMonologue | null;
  by_speaker?: Record<string, number>;
};

export type ConversationFto = {
  available: boolean;
  reason?: string;
  overall_median_seconds?: number | null;
  by_speaker?: Record<string, number>;
  by_pair?: Record<string, number>;
  count?: number;
};

export type ConversationInterruptionBySpeaker = {
  made: number;
  received: number;
  ratio: number | null;
};

export type ConversationInterruptions = {
  available: boolean;
  reason?: string;
  total?: number;
  backchannel_excluded?: number;
  /** Ключ "SPEAKER_00→SPEAKER_01" — кто перебил → кого перебили. */
  by_pair?: Record<string, number>;
  by_speaker?: Record<string, ConversationInterruptionBySpeaker>;
};

export type InteractionMatrixEdge = {
  from: string;
  to: string;
  count: number;
};

export type ConversationInteractionMatrix = {
  edges: InteractionMatrixEdge[];
  top_links: InteractionMatrixEdge[];
  in_degree: Record<string, number>;
  out_degree: Record<string, number>;
};

export type ConversationDominance = {
  by_speaker: Record<string, number>;
  most_dominant: string | null;
  interruptions_included: boolean;
};

export type BalanceDynamicsBucket = {
  start_seconds: number;
  end_seconds: number;
  by_speaker_seconds: Record<string, number>;
  by_speaker_percent: Record<string, number>;
};

export type ConversationBalanceDynamics = {
  bucket_seconds: number;
  buckets: BalanceDynamicsBucket[];
};

export type ConversationDeadAir = {
  total_seconds: number;
  percent: number;
  meeting_span_seconds: number;
  precision: string;
};

export type ConversationMetricsMeta = {
  segments_count: number;
  precise_timing_available: boolean;
  speakers: string[];
};

/** GET /meetings/{id}/details → conversation_metrics (задача B1). */
export type ConversationMetrics = {
  talk_ratio: ConversationTalkRatio;
  turns: ConversationTurns;
  fto: ConversationFto;
  interruptions: ConversationInterruptions;
  interaction_matrix: ConversationInteractionMatrix;
  dominance: ConversationDominance;
  balance_dynamics: ConversationBalanceDynamics;
  dead_air: ConversationDeadAir;
  meta: ConversationMetricsMeta;
};

/** GET /meetings/{id}/details → filler_stats[speaker_label] (задача H1). */
export type FillerStatsEntry = {
  total: number;
  per_minute: number;
  top_words: string[];
};

export type FillerStats = Record<string, FillerStatsEntry>;

export type EmotionTimelineEntry = {
  start: number;
  end: number;
  speaker: string;
  /** DUSHA: neutral / angry / positive / sad / other */
  emotion: string;
  confidence: number;
};

export type EmotionSummary = {
  dominant: string | null;
  negative_share_pct: number;
};

/** GET /meetings/{id}/details → emotion_analysis (задача B2). */
export type EmotionAnalysis = {
  available: boolean;
  reason?: string;
  timeline: EmotionTimelineEntry[];
  by_speaker: Record<string, Record<string, number>>;
  summary: EmotionSummary;
};

export type ProsodySpeakerMetrics = {
  f0_median_hz: number | null;
  f0_variability: number | null;
  tempo_words_per_sec: number | null;
  jitter_median_pct: number | null;
  shimmer_median_pct: number | null;
  hnr_median_db: number | null;
  segments_analyzed: number;
  monotone: boolean;
};

export type ProsodyFatigueEntry = {
  jitter_delta_pct: number | null;
  f0_variability_delta: number | null;
  tempo_delta_words_per_sec: number | null;
  markers_triggered: number;
  fatigue_detected: boolean;
};

export type ProsodyHesitationEntry = {
  count: number;
  avg_latency_seconds: number;
};

/** GET /meetings/{id}/details → prosody_analysis (задача B2). */
export type ProsodyAnalysis = {
  available: boolean;
  reason?: string;
  by_speaker: Record<string, ProsodySpeakerMetrics>;
  fatigue: {
    by_speaker: Record<string, ProsodyFatigueEntry>;
    thresholds?: Record<string, number>;
  };
  hesitations: {
    by_speaker: Record<string, ProsodyHesitationEntry>;
    threshold_seconds?: number | null;
  };
  diagnostics?: {
    torchcrepe_available: boolean;
    vocal_features_available: boolean;
  };
};

export type RecomputeAnalyticsResult = {
  meeting_id: string;
  conversation_metrics: ConversationMetrics;
  emotion_analysis?: EmotionAnalysis;
  prosody_analysis?: ProsodyAnalysis;
};

export type ChatStructuredResponse = {
  intro: string;
  items: {
    num: number;
    responsible: string;
    text: string;
    deadline: string;
    deadline_iso?: string | null;
  }[];
  sources: string[];
  urgent_count: number;
};

export type StructuredTask = {
  task: string;
  assignee: string;
  deadline: string;
  priority: string;
  timestamp?: string;
};

export type MeetingDetails = {
  id: string;
  name: string;
  date: string;
  duration?: string;
  status: string;
  protocol?: string;
  protocol_participants_count?: number;
  protocol_speeches_count?: number;
  transcript?: string;
  summary?: string;
  summary_structured?: SummaryStructured;
  structured_tasks?: StructuredTask[];
  insights?: MeetingInsights | null;
  slide_timeline?: SlideTimelineEntry[];
  presentation_slides?: SlideTimelineEntry[];
  presentation_decks?: PresentationDeck[];
  slide_deck_summary?: SlideDeckSummary | null;
  speaker_genders?: Record<string, SpeakerGenderInfo>;
  video_analysis?: VideoAnalysisMeta | null;
  video_screen_insights?: VideoScreenInsights | null;
  transcription?: string;
  speaker_languages?: Record<string, string>;
  is_favorite?: boolean;
  has_img?: boolean;
  has_video_file?: boolean;
  pipeline?: string | null;
  file_path?: string;
  /** Задача B1: отсутствует у старых встреч — см. POST /analytics/recompute. */
  conversation_metrics?: ConversationMetrics | null;
  /** Задача H1: отсутствует у старых встреч — считается в пайплайне после постпроцесса сегментов. */
  filler_stats?: FillerStats | null;
  /** Задача B2: отсутствует у старых встреч — см. POST /analytics/recompute?voice=true. */
  emotion_analysis?: EmotionAnalysis | null;
  prosody_analysis?: ProsodyAnalysis | null;
};

export type MeetingSegmentsResponse = {
  meeting_id: string;
  total_segments: number;
  speaker_order?: string[];
  speaker_stats?: Record<string, { count: number; max_time: number }>;
  speaker_languages?: Record<string, string>;
  kazakh_retranscribe_pending?: boolean;
  kazakh_ru_pending?: boolean;
  kazakh_audio_retranscribe_pending?: boolean;
  /** Задача H1: true, если сегменты в ответе прошли инлайн-чистку паразитов (?clean=true). */
  clean_applied?: boolean;
  slide_timeline?: SlideTimelineEntry[];
  presentation_slides?: SlideTimelineEntry[];
  presentation_decks?: PresentationDeck[];
  speaker_genders?: Record<string, SpeakerGenderInfo>;
  video_analysis?: VideoAnalysisMeta | null;
  segments: SpeechSegment[];
};

export type UploadResult = {
  meeting_id: string;
  name: string;
  status: string;
  message: string;
};

export type SaveMeetingResult = {
  ok: boolean;
  database: boolean;
  file_on_disk: boolean;
  meeting_id: string;
  name?: string;
  status?: string;
  message: string;
};

export type ProcessAudioStartResult = {
  meeting_id: string;
  name: string;
  status: string;
  progress: number;
  message: string;
};

export type ProcessingStep = {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'done';
};

/** Задача L1: нормализованный этап степпера — единый для всех пайплайнов
 *  (см. backend/app/services/meeting_progress_eta.py::build_progress_stages). */
export type ProcessingStageInfo = {
  key: string;
  title: string;
  state: 'done' | 'active' | 'pending';
};

export type MeetingProgress = {
  meeting_id: string;
  progress: number;
  stage: string;
  message: string;
  status: string;
  pipeline?: 'fast' | 'diarization' | 'deepgram' | 'pyannote' | 'video';
  steps?: ProcessingStep[];
  /** Задача L1: секунд с начала обработки; null, пока пайплайн ещё не записал started_at. */
  elapsed_sec?: number | null;
  /** Задача L1: оценка оставшегося времени в секундах; null при progress < 5% или когда
   *  данных недостаточно — фронт должен показать «оцениваем...». */
  eta_sec?: number | null;
  /** Задача L1: нормализованный список этапов для степпера обработки. */
  stages?: ProcessingStageInfo[];
};

export type ProcessingPipeline = 'fast' | 'pyannote' | 'deepgram' | 'video';

export type VideoProcessingOptions = {
  gender: boolean;
  tone: boolean;
  slides: boolean;
  ai_protocol: boolean;
  ai_summary: boolean;
  ai_tasks: boolean;
  translate_to: string | null;
};

export type SpeechSegment = {
  id: number;
  start_time: string;
  end_time: string;
  start_seconds?: number;
  end_seconds?: number;
  duration_seconds: number;
  text: string;
  speaker_display?: string;
  speaker_name?: string;
  speaker_label?: string;
  speaker_position?: string;
  speaker_gender?: string;
  speaker_language?: string;
  detected_language?: string;
  text_ru?: string;
  text_kk?: string;
  is_speech?: boolean;
};

export function normalizeSegments(data: unknown): SpeechSegment[] {
  if (Array.isArray(data)) return data as SpeechSegment[];
  if (data && typeof data === 'object' && Array.isArray((data as MeetingSegmentsResponse).segments)) {
    return (data as MeetingSegmentsResponse).segments;
  }
  return [];
}

function parseApiError(text: string, fallback: string): string {
  try {
    const parsed = JSON.parse(text) as { detail?: string | unknown };
    if (parsed.detail) {
      return typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail);
    }
  } catch {
    // not JSON
  }
  return text || fallback;
}

/** Ошибка API с сохранённым HTTP-статусом — нужна там, где UI ведёт себя по-разному
 *  в зависимости от кода (напр. 403 «не владелец» в шаринге встреч показывает отдельный
 *  экран, а не toast). Остаётся совместимой со старым `e instanceof Error` кодом. */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function handleResponse<T>(res: Response, fallbackMessage: string): Promise<T> {
  if (!res.ok) {
    const errorText = await res.text();
    throw new ApiError(parseApiError(errorText, `${fallbackMessage}: ${res.status}`), res.status);
  }
  return res.json() as Promise<T>;
}

const PROTOCOL_GENERATION_TIMEOUT_MS = 25 * 60 * 1000;
const PROTOCOL_POLL_INTERVAL_MS = 3000;

export type RegenerateStatus = {
  meeting_id?: string;
  status: 'idle' | 'generating' | 'done' | 'error';
  message?: string;
  protocol?: string;
  protocol_len?: number;
  protocol_participants_count?: number;
  protocol_speeches_count?: number;
  started_at?: string;
  finished_at?: string;
};

export type ProtocolProgressExtra = {
  protocol?: string;
  protocol_participants_count?: number;
  protocol_speeches_count?: number;
};

// ===== Отделы и приоритеты (задача P2, backend/app/api/departments.py + admin.py) =====

/** Отдел компании — задаёт приоритет обработки встреч владельцев в очереди по умолчанию. */
export type DepartmentItem = {
  id: number;
  name: string;
  priority: number;
  /** Число пользователей, состоящих в отделе. */
  user_count: number;
};

export type DepartmentCreatePayload = {
  name: string;
  priority?: number;
};

export type DepartmentUpdatePayload = {
  name?: string;
  priority?: number;
};

/** Пользователь из GET /api/admin/users, расширенный полями отдела/личного приоритета. */
export type AdminUserItem = {
  id: number;
  email: string;
  full_name?: string | null;
  role: string;
  is_active: boolean;
  email_verified: boolean;
  last_login?: string | null;
  created_at: string;
  keycloak_id?: string | null;
  department_id?: number | null;
  /** null = приоритет наследуется от отдела/дефолта — см. meeting_priority.py. */
  priority_override?: number | null;
};

// ===== Глоссарий терминов (задача GL1, admin-only, backend/app/api/glossary.py) =====
// НЕ ПУТАТЬ с полем `glossary?` на VideoInsightsResponse ниже — это термины видео-инсайтов,
// отдельная сущность, к этому блоку не относится.

export type GlossaryLanguage = 'ru' | 'kk' | 'en';

/** Термин глоссария — врезается в подсказку Whisper и LLM-постобработку (см. glossary_service).
 *  GlossaryTermResponse на бэке не отдаёт created_at/updated_at — намеренно не добавляем их сюда. */
export type GlossaryTermItem = {
  id: number;
  /** Канонический термин, напр. «АСОУП». */
  term: string;
  /** Расшифровка/описание термина. */
  description?: string | null;
  /** Частые варианты ошибочного распознавания, напр. ["асопе", "asope"]. */
  aliases: string[];
  /** null = термин применяется для всех языков. */
  language?: GlossaryLanguage | null;
  is_active: boolean;
};

export type GlossaryTermCreatePayload = {
  term: string;
  description?: string | null;
  aliases?: string[];
  language?: GlossaryLanguage | null;
  is_active?: boolean;
};

/** Все поля опциональны — PUT обновляет только переданные (см. GlossaryTermUpdate на бэке). */
export type GlossaryTermUpdatePayload = {
  term?: string;
  description?: string | null;
  aliases?: string[];
  language?: GlossaryLanguage | null;
  is_active?: boolean;
};

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function xhrUpload(
  url: string,
  formData: FormData,
  onProgress: (percent: number) => void,
): Promise<ProcessAudioStartResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    const token = getToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as ProcessAudioStartResult);
        } catch {
          reject(new Error('Некорректный ответ сервера'));
        }
        return;
      }
      reject(new Error(parseApiError(xhr.responseText, `Ошибка загрузки: ${xhr.status}`)));
    };
    xhr.onerror = () => reject(new Error('Сетевая ошибка при загрузке файла'));
    xhr.onabort = () => reject(new Error('Загрузка отменена'));
    xhr.send(formData);
  });
}

async function pollMeetingProgress(
  meetingId: string,
  onProgress: (data: MeetingProgress) => void,
  intervalMs = 1500,
  maxWaitMs = 60 * 60 * 1000,
): Promise<MeetingProgress> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < maxWaitMs) {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/progress`);
    const data = await handleResponse<MeetingProgress>(res, 'Ошибка получения прогресса');
    onProgress(data);

    if (data.status === 'completed') {
      return data;
    }
    if (data.status === 'error') {
      throw new Error(data.message || 'Ошибка обработки аудио');
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error('Превышено время ожидания обработки');
}

export const apiService = {
  /** Загрузка + фоновая обработка. diarize=true — полный pipeline со спикерами. */
  async processAudio(
    file: File,
    name: string,
    participants: { name: string; position?: string; language?: string }[] = [],
    options?: {
      pipeline?: ProcessingPipeline;
      onUploadProgress?: (percent: number) => void;
      onProcessingProgress?: (data: MeetingProgress) => void;
    },
  ): Promise<{ meeting_id: string }> {
    const pipeline = options?.pipeline ?? 'fast';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('pipeline', pipeline);
    formData.append('diarize', pipeline === 'pyannote' ? 'true' : 'false');
    if (participants.length > 0) {
      formData.append('speakers', JSON.stringify(participants));
    }

    const start = await xhrUpload(
      `${API_BASE_URL}/api/meetings/audio/process`,
      formData,
      options?.onUploadProgress ?? (() => {}),
    );

    // Не ждём обработку здесь: файл принят и поставлен в очередь — прогресс
    // (процент/этапы/ETA) пользователь смотрит в разделе «Записи». Возвращаем
    // meeting_id сразу после загрузки.
    return { meeting_id: start.meeting_id };
  },

  async processVideo(
    file: File,
    name: string,
    participants: { name: string; position?: string; language?: string }[] = [],
    options?: {
      videoOptions?: VideoProcessingOptions;
      onUploadProgress?: (percent: number) => void;
      onProcessingProgress?: (data: MeetingProgress) => void;
    },
  ): Promise<{ meeting_id: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('options', JSON.stringify(options?.videoOptions ?? {
      gender: true,
      tone: true,
      slides: false,
      ai_protocol: true,
      ai_summary: true,
      ai_tasks: true,
      translate_to: null,
    }));
    if (participants.length > 0) {
      formData.append('speakers', JSON.stringify(participants));
    }

    const start = await xhrUpload(
      `${API_BASE_URL}/api/meetings/video/process`,
      formData,
      options?.onUploadProgress ?? (() => {}),
    );

    // Как и в processAudio: не ждём обработку — файл в очереди, прогресс в «Записях».
    return { meeting_id: start.meeting_id };
  },

  async uploadMeeting(file: File, name: string, speakerNames: string[] = []): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    if (speakerNames.length > 0) {
      formData.append('speakers', JSON.stringify(speakerNames));
    }

    const res = await apiFetch(`${API_BASE_URL}/api/meetings/upload`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse<UploadResult>(res, 'Ошибка загрузки');
  },

  async getMeetings(): Promise<MeetingListItem[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/`);
    return handleResponse<MeetingListItem[]>(res, 'Ошибка загрузки встреч');
  },

  async getMeetingDetails(id: string): Promise<MeetingDetails> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/details?t=${Date.now()}`, {
      cache: 'no-store',
    });
    return handleResponse<MeetingDetails>(res, 'Встреча не найдена');
  },

  async saveMeetingToDb(
    id: string,
    payload: {
      protocol?: string;
      summary?: string;
      summary_structured?: SummaryStructured | null;
      transcription?: string;
      structured_tasks?: StructuredTask[];
    },
  ): Promise<SaveMeetingResult> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<SaveMeetingResult>(res, 'Ошибка сохранения в БД');
  },

  async getMeetingProgress(id: string): Promise<MeetingProgress> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/progress`);
    return handleResponse<MeetingProgress>(res, 'Ошибка получения прогресса');
  },

  async getMeetingSegments(
    id: string,
    opts?: { clean?: boolean },
  ): Promise<MeetingSegmentsResponse> {
    const cleanQuery = opts?.clean ? '&clean=true' : '';
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/segments?t=${Date.now()}${cleanQuery}`, {
      cache: 'no-store',
    });
    return handleResponse<MeetingSegmentsResponse>(res, 'Ошибка загрузки сегментов');
  },

  async updateSpeakerLanguages(
    id: string,
    speakerLanguages: Record<string, string>,
  ): Promise<{ speaker_languages: Record<string, string>; auto_retranscribe?: boolean }> {
    return this.updateSpeakerSettings(id, speakerLanguages, {});
  },

  async updateSpeakerSettings(
    id: string,
    speakerLanguages: Record<string, string>,
    speakerGenders: Record<string, string> = {},
  ): Promise<{
    speaker_languages: Record<string, string>;
    speaker_genders?: Record<string, SpeakerGenderInfo>;
    auto_retranscribe?: boolean;
  }> {
    const body: Record<string, unknown> = {};
    if (Object.keys(speakerLanguages).length) body.speaker_languages = speakerLanguages;
    if (Object.keys(speakerGenders).length) body.speaker_genders = speakerGenders;
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return handleResponse(res, 'Ошибка сохранения настроек спикеров');
  },

  async retranscribeMeeting(id: string): Promise<{ status: string; message: string }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/retranscribe`, { method: 'POST' });
    return handleResponse(res, 'Ошибка перетранскрибации');
  },

  /** Повторная диаризация + идентификация спикеров (Pyannote + Qdrant). */
  async reprocessDeepgramMeeting(
    id: string,
    onProgress?: (data: MeetingProgress) => void,
  ): Promise<MeetingProgress> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/reprocess-deepgram`, { method: 'POST' });
    await handleResponse(res, 'Ошибка повторной обработки');
    return pollMeetingProgress(id, onProgress ?? (() => {}), 2000, 60 * 60 * 1000);
  },

  async rediarizeMeeting(
    id: string,
    onProgress?: (data: MeetingProgress) => void,
  ): Promise<MeetingProgress> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/rediarize`, { method: 'POST' });
    await handleResponse(res, 'Ошибка повторной диаризации');
    return pollMeetingProgress(id, onProgress ?? (() => {}), 2000, 60 * 60 * 1000);
  },

  async translateKzRu(id: string): Promise<{ status: string; message: string; updated?: number }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/translate-kz-ru`, { method: 'POST' });
    return handleResponse(res, 'Ошибка перевода KZ→RU');
  },

  async fillMeetingGaps(id: string): Promise<{ status: string; message: string }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/fill-gaps`, { method: 'POST' });
    return handleResponse(res, 'Ошибка дозаполнения пропусков');
  },

  /** Задача H1: аудио без длинных пауз — <audio src>, токен передаётся query-параметром. */
  getMeetingAudioCondensedUrl(id: string): string {
    return withAccessToken(`${API_BASE_URL}/api/meetings/${id}/audio/condensed`);
  },

  /** Длительности «57 мин → 41 мин» — первая генерация (ffmpeg silenceremove) может занять
   *  десятки секунд, дальше кэшируется на бэкенде. */
  async getMeetingAudioCondensedInfo(id: string): Promise<{ original_sec: number; condensed_sec: number }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/audio/condensed/info`);
    return handleResponse(res, 'Не удалось подготовить сжатое аудио');
  },

  /** Задача B1/B2: пересчёт разговорных метрик (+эмоции/просодика при voice=true) для
   *  встреч, обработанных до появления этих метрик — из уже сохранённых в БД сегментов. */
  async recomputeMeetingAnalytics(id: string, voice = true): Promise<RecomputeAnalyticsResult> {
    const res = await apiFetch(
      `${API_BASE_URL}/api/meetings/${id}/analytics/recompute?voice=${voice ? 'true' : 'false'}`,
      { method: 'POST' },
    );
    return handleResponse<RecomputeAnalyticsResult>(res, 'Ошибка пересчёта аналитики');
  },

  async detectSpeakerLanguages(id: string): Promise<{
    speaker_languages: Record<string, string>;
    detected: Record<string, string>;
    auto_retranscribe?: boolean;
  }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/detect-speaker-languages`, {
      method: 'POST',
    });
    return handleResponse(res, 'Ошибка автоопределения языков');
  },

  async importTeamsTranscript(
    id: string,
    transcript: string,
  ): Promise<{
    teams_speakers: Array<{
      name: string;
      utterances: number;
      chars: number;
      language_suggested: string;
      sample?: string;
    }>;
    mapping: Record<string, string>;
    speaker_languages: Record<string, string>;
    meeting_speakers: string[];
    auto_retranscribe?: boolean;
  }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/import-teams-transcript`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transcript, apply_names: true, apply_languages: true }),
    });
    return handleResponse(res, 'Ошибка импорта Teams');
  },

  async getChatSuggestions(
    id: string,
    section = 'general',
  ): Promise<{ suggestions: string[]; section?: string }> {
    const params = new URLSearchParams({
      t: String(Date.now()),
      section,
    });
    const res = await apiFetch(
      `${API_BASE_URL}/api/meetings/${id}/chat-suggestions?${params.toString()}`,
      { cache: 'no-store' },
    );
    return handleResponse(res, 'Ошибка загрузки подсказок чата');
  },

  async getMeetingInsights(id: string): Promise<MeetingInsights> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/insights`);
    return handleResponse<MeetingInsights>(res, 'Ошибка загрузки инсайтов');
  },

  async getInsightsRefreshStatus(id: string): Promise<InsightsRefreshStatus> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/insights/status`);
    return handleResponse<InsightsRefreshStatus>(res, 'Ошибка статуса анализа');
  },

  async refreshMeetingInsights(id: string): Promise<MeetingInsights> {
    const startRes = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/insights/refresh`, {
      method: 'POST',
    });
    const started = await handleResponse<InsightsRefreshStatus & MeetingInsights>(
      startRes,
      'Ошибка обновления анализа',
    );

    // Старый синхронный backend: сразу вернул insights
    if (started.tone_segments?.length) {
      return started as MeetingInsights;
    }

    if (started.status !== 'generating') {
      if (started.insights) return started.insights;
      throw new Error(started.message || 'Не удалось запустить анализ');
    }

    const deadline = Date.now() + 5 * 60 * 1000;
    const runStartedAt = started.started_at
      ? Date.parse(started.started_at)
      : Date.now();

    while (Date.now() < deadline) {
      const status = await this.getInsightsRefreshStatus(id);
      if (status.status === 'error') {
        throw new Error(status.message || 'Ошибка анализа');
      }
      if (status.status === 'done' && status.insights) {
        const finishedAt = status.finished_at ? Date.parse(status.finished_at) : Date.now();
        if (finishedAt >= runStartedAt - 2000) {
          return status.insights;
        }
      }
      await sleep(2500);
    }
    throw new Error('Анализ занял больше 5 минут. Попробуйте ещё раз.');
  },

  async getRegenerateStatus(id: string): Promise<RegenerateStatus> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/regenerate-status`);
    return handleResponse<RegenerateStatus>(res, 'Ошибка статуса регенерации');
  },

  async watchProtocolRegeneration(
    id: string,
    onProgress?: (message: string, extra?: ProtocolProgressExtra) => void,
  ): Promise<{ protocol: string }> {
    const loadProtocolFromDetails = async (): Promise<string> => {
      const details = await this.getMeetingDetails(id);
      return details.protocol || details.transcript || '';
    };

    const emitProgress = (status: RegenerateStatus) => {
      if (!status.message && !status.protocol) return;
      onProgress?.(status.message || 'Генерация протокола…', {
        protocol: status.protocol,
        protocol_participants_count: status.protocol_participants_count,
        protocol_speeches_count: status.protocol_speeches_count,
      });
    };

    const deadline = Date.now() + PROTOCOL_GENERATION_TIMEOUT_MS;
    const startedAt = Date.now();
    let sawGenerating = false;

    while (Date.now() < deadline) {
      const status = await this.getRegenerateStatus(id);
      emitProgress(status);

      if (status.status === 'generating') {
        sawGenerating = true;
        await sleep(PROTOCOL_POLL_INTERVAL_MS);
        continue;
      }
      if (status.status === 'done') {
        const protocol = status.protocol || await loadProtocolFromDetails();
        if (protocol) return { protocol };
      }
      if (status.status === 'error') {
        throw new Error(status.message || 'Ошибка генерации протокола');
      }
      if (status.status === 'idle' && sawGenerating && Date.now() - startedAt > PROTOCOL_POLL_INTERVAL_MS * 2) {
        const protocol = await loadProtocolFromDetails();
        if (protocol) return { protocol };
      }
      if (status.status === 'idle' && !sawGenerating) {
        const protocol = await loadProtocolFromDetails();
        if (protocol) return { protocol };
        throw new Error('Генерация протокола не запущена');
      }
      await sleep(PROTOCOL_POLL_INTERVAL_MS);
    }

    const fallback = await loadProtocolFromDetails();
    if (fallback) return { protocol: fallback };

    throw new Error(
      'Генерация заняла слишком много времени (более 15 мин). Обновите страницу — протокол мог сохраниться на сервере.',
    );
  },

  async generateMeetingProtocol(
    id: string,
    onProgress?: (message: string, extra?: ProtocolProgressExtra) => void,
  ): Promise<{ protocol: string }> {
    const startRes = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/generate-protocol`, {
      method: 'POST',
    });
    const startData = await handleResponse<RegenerateStatus>(startRes, 'Ошибка запуска генерации');

    const emitProgress = (status: RegenerateStatus) => {
      if (!status.message && !status.protocol) return;
      onProgress?.(status.message || 'Генерация протокола…', {
        protocol: status.protocol,
        protocol_participants_count: status.protocol_participants_count,
        protocol_speeches_count: status.protocol_speeches_count,
      });
    };

    if (startData.status === 'done' && startData.protocol) {
      const details = await this.getMeetingDetails(id);
      const fresh = details.protocol || details.transcript || '';
      return { protocol: fresh || startData.protocol };
    }

    emitProgress(startData);
    if (startData.status !== 'generating') {
      const details = await this.getMeetingDetails(id);
      const protocol = details.protocol || details.transcript || '';
      if (protocol) return { protocol };
    }

    return this.watchProtocolRegeneration(id, onProgress);
  },

  async generateMeetingSummary(id: string): Promise<{ summary: string }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/generate-summary`, {
      method: 'POST',
    });
    return handleResponse<{ summary: string }>(res, 'Ошибка генерации конспекта');
  },

  async chatWithMeeting(
    id: string,
    message: string,
  ): Promise<{ response: string; structured?: ChatStructuredResponse | null }> {
    const formData = new FormData();
    formData.append('message', message);
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/chat`, {
      method: 'POST',
      body: formData,
    });
    const data = await handleResponse<{
      response: string;
      structured?: ChatStructuredResponse | null;
      message?: string;
    }>(res, 'Ошибка AI чата');
    return { response: data.response || '', structured: data.structured ?? null };
  },

  getMeetingAudioUrl(id: string): string {
    return withAccessToken(`${API_BASE_URL}/api/meetings/${id}/audio`);
  },

  getMeetingVideoUrl(id: string): string {
    return withAccessToken(`${API_BASE_URL}/api/meetings/${id}/video`);
  },

  async attachMeetingVideo(meetingId: string, file: File): Promise<{ ok: boolean; has_video_file: boolean }> {
    const form = new FormData();
    form.append('file', file);
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/attach-video`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(parseAttachVideoError(res.status, (err as { detail?: unknown }).detail));
    }
    return res.json();
  },

  async checkMeetingVideoAvailable(meetingId: string): Promise<boolean> {
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/details?t=${Date.now()}`);
      if (!res.ok) return false;
      const data = await res.json() as { has_video_file?: boolean };
      return data.has_video_file === true;
    } catch {
      return false;
    }
  },

  async analyzeMeetingSlidesVlm(meetingId: string): Promise<{ status: string; message?: string }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/video/analyze-slides`, {
      method: 'POST',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const detail = (err as { detail?: string }).detail;
      throw new Error(typeof detail === 'string' ? detail : 'Не удалось запустить анализ слайдов');
    }
    return res.json();
  },

  async getMeetingSlidesVlmStatus(meetingId: string): Promise<{
    status: string;
    message?: string;
    frames?: number;
  }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/video/analyze-slides/status`);
    if (!res.ok) {
      throw new Error('Не удалось получить статус анализа');
    }
    return res.json();
  },

  getMeetingFrameUrl(meetingId: string, frameIndex: number, crop = false): string {
    const q = crop ? '?crop=1' : '';
    return withAccessToken(`${API_BASE_URL}/api/meetings/${meetingId}/frames/${frameIndex}${q}`);
  },

  async summarizeMeetingSlides(meetingId: string): Promise<{
    slide_timeline: SlideTimelineEntry[];
    presentation_slides?: SlideTimelineEntry[];
    slide_deck_summary: SlideDeckSummary;
  }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/video/slides/summarize`, {
      method: 'POST',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Ошибка суммаризации слайдов');
    }
    return res.json();
  },

  getMeetingSlidesPptxUrl(meetingId: string): string {
    return withAccessToken(`${API_BASE_URL}/api/meetings/${meetingId}/video/slides/pptx`);
  },

  async downloadMeetingSlidesPptx(meetingId: string, meetingName: string): Promise<void> {
    // Сырой URL без access_token в query: apiFetch уже добавляет Authorization-заголовок,
    // дублировать токен в query не нужно (getMeetingSlidesPptxUrl — только для src/href).
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/video/slides/pptx`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Ошибка генерации PPTX');
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${meetingName || 'meeting'}_slides.pptx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  async downloadMeetingPdf(
    id: string,
    doc: 'protocol' | 'summary' | 'transcription' | 'stats' | 'tasks' | 'video_insights' | 'all',
  ): Promise<{ blob: Blob; filename: string }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/export/pdf?doc=${doc}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Ошибка скачивания PDF');
    }
    const blob = await res.blob();
    const xName = res.headers.get('X-Export-Filename');
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = xName || match?.[1] || `meeting_${doc}.pdf`;
    return { blob, filename };
  },

  async downloadMeetingInsightsPptx(id: string): Promise<{ blob: Blob; filename: string }> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}/export/pptx?doc=video_insights`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { detail?: string }).detail || 'Ошибка скачивания PPTX');
    }
    const blob = await res.blob();
    const xName = res.headers.get('X-Export-Filename');
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = xName || match?.[1] || 'AI_video_insights.pptx';
    return { blob, filename };
  },

  async deleteMeeting(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Ошибка удаления встречи');
  },

  /** Кто имеет доступ к встрече. 403 — текущий пользователь не владелец/elevated. */
  async getMeetingShares(meetingId: string): Promise<ShareUser[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/shares`);
    return handleResponse<ShareUser[]>(res, 'Не удалось загрузить список доступа');
  },

  /** Выдать доступ по user_ids и/или emails. Возвращает обновлённый список ShareUser.
   *  Неизвестные email backend молча игнорирует (без 404) — сверяйте результат с payload,
   *  чтобы показать пользователю осмысленную обратную связь. */
  async addMeetingShares(
    meetingId: string,
    payload: { user_ids?: number[]; emails?: string[] },
  ): Promise<ShareUser[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/shares`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<ShareUser[]>(res, 'Не удалось добавить доступ');
  },

  async removeMeetingShare(meetingId: string, userId: number): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/shares/${userId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const errorText = await res.text();
      throw new ApiError(parseApiError(errorText, 'Не удалось отозвать доступ'), res.status);
    }
  },

  /** Встречи, расшаренные текущему пользователю (вкладка «Доступные мне»). */
  async getSharedWithMe(): Promise<SharedMeeting[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/shared-with-me`);
    return handleResponse<SharedMeeting[]>(res, 'Не удалось загрузить список доступных встреч');
  },

  /** Список пользователей для выбора в модалке шаринга — доступен только admin.
   *  403 (обычный пользователь) — штатный случай, возвращаем null для fallback на email-инпут. */
  async getUsersForSharing(): Promise<ShareUser[] | null> {
    const res = await apiFetch(`${API_BASE_URL}/api/admin/users`);
    if (res.status === 403) return null;
    if (!res.ok) {
      const errorText = await res.text();
      throw new ApiError(parseApiError(errorText, 'Не удалось загрузить список пользователей'), res.status);
    }
    const data = await res.json() as { id: number; email: string; full_name?: string | null }[];
    return data.map(u => ({ id: u.id, email: u.email, full_name: u.full_name ?? null }));
  },

  // ===== Задачи (задача C1) =====

  /** Страница задач с фильтрами. limit на бэкенде ≤ 100. */
  async getTasks(filters: TaskFilters = {}): Promise<TaskListResult> {
    const params = new URLSearchParams();
    if (filters.project) params.set('project', filters.project);
    if (filters.status) params.set('status', filters.status);
    if (filters.assignee_id != null) params.set('assignee_id', String(filters.assignee_id));
    if (filters.meeting_id) params.set('meeting_id', filters.meeting_id);
    if (filters.mine) params.set('mine', 'true');
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    const res = await apiFetch(`${API_BASE_URL}/api/tasks/${qs ? `?${qs}` : ''}`);
    return handleResponse<TaskListResult>(res, 'Не удалось загрузить задачи');
  },

  /** Все задачи с учётом фильтров — постранично добирает до total (для канбан-доски). */
  async getAllTasks(filters: Omit<TaskFilters, 'page' | 'limit'> = {}): Promise<TaskItem[]> {
    const limit = 100;
    const first = await this.getTasks({ ...filters, page: 1, limit });
    const all = [...first.tasks];
    let page = 1;
    while (all.length < first.total && first.tasks.length > 0) {
      page += 1;
      const next = await this.getTasks({ ...filters, page, limit });
      if (!next.tasks.length) break;
      all.push(...next.tasks);
    }
    return all;
  },

  async createTask(payload: TaskCreatePayload): Promise<TaskItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/tasks/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<TaskItem>(res, 'Не удалось создать задачу');
  },

  async updateTask(id: number, payload: TaskUpdatePayload): Promise<TaskItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/tasks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<TaskItem>(res, 'Не удалось обновить задачу');
  },

  async deleteTask(id: number): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/api/tasks/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const errorText = await res.text();
      throw new ApiError(parseApiError(errorText, 'Не удалось удалить задачу'), res.status);
    }
  },

  /** Смена статуса (drag&drop между колонками). status — query-параметр бэкенда. */
  async updateTaskStatus(id: number, status: TaskStatus): Promise<TaskItem> {
    const res = await apiFetch(
      `${API_BASE_URL}/api/tasks/${id}/status?status=${encodeURIComponent(status)}`,
      { method: 'PATCH' },
    );
    return handleResponse<TaskItem>(res, 'Не удалось изменить статус задачи');
  },

  /** Перенос в другую колонку с позицией (бэкенд сам сдвигает соседние задачи). */
  async moveTask(id: number, newStatus: TaskStatus, newPosition: number): Promise<TaskItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/tasks/${id}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_status: newStatus, new_position: newPosition }),
    });
    return handleResponse<TaskItem>(res, 'Не удалось переместить задачу');
  },

  /** Переупорядочивание внутри колонки: позиции = индексы в переданном списке id. */
  async reorderTasks(taskIds: number[]): Promise<TaskItem[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/tasks/reorder`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_ids: taskIds }),
    });
    return handleResponse<TaskItem[]>(res, 'Не удалось изменить порядок задач');
  },

  /** Список проектов (значения поля project) — группы канбан-доски. */
  async getTaskProjects(): Promise<string[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/tasks/projects/list`);
    return handleResponse<string[]>(res, 'Не удалось загрузить список проектов');
  },

  /** Материализация поручений встречи в задачи. 403 — не владелец и не admin/manager. */
  async createTasksFromActionItems(
    meetingId: string,
    items: ActionItemTaskPayload[],
  ): Promise<TaskItem[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/meetings/${meetingId}/tasks/from-action-items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    return handleResponse<TaskItem[]>(res, 'Не удалось создать задачи из поручений');
  },

  async getSpeakers(): Promise<SpeakerListItem[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/speakers/`);
    return handleResponse<SpeakerListItem[]>(res, 'Ошибка загрузки спикеров');
  },

  getSpeakerAudioUrl(id: number | string): string {
    return withAccessToken(`${API_BASE_URL}/api/speakers/${id}/audio`);
  },

  async createSpeaker(
    name: string,
    position: string,
    voiceFile: File,
  ): Promise<{ id: number; name: string }> {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('position', position);
    formData.append('voice_file', voiceFile);
    const res = await apiFetch(`${API_BASE_URL}/api/speakers/`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(res, 'Ошибка добавления спикера');
  },

  async updateSpeaker(id: number | string, name: string, position: string): Promise<void> {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('position', position);
    const res = await apiFetch(`${API_BASE_URL}/api/speakers/${id}`, {
      method: 'PUT',
      body: formData,
    });
    await handleResponse(res, 'Ошибка обновления спикера');
  },

  async deleteSpeaker(id: number | string): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/api/speakers/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.text();
      throw new Error(parseApiError(err, 'Ошибка удаления спикера'));
    }
  },

  // ===== «Мой голос» (задача SP1, backend/app/api/speakers.py: /my-voice) =====

  async getMyVoice(): Promise<MyVoiceStatus> {
    const res = await apiFetch(`${API_BASE_URL}/api/speakers/my-voice`);
    return handleResponse<MyVoiceStatus>(res, 'Не удалось загрузить голосовой профиль');
  },

  /** Записать/перезаписать голосовой профиль текущего пользователя (микрофон или файл). */
  async uploadMyVoice(voiceFile: File): Promise<MyVoiceStatus> {
    const formData = new FormData();
    formData.append('voice_file', voiceFile);
    const res = await apiFetch(`${API_BASE_URL}/api/speakers/my-voice`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse<MyVoiceStatus>(res, 'Не удалось записать голос');
  },

  async deleteMyVoice(): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/api/speakers/my-voice`, { method: 'DELETE' });
    if (!res.ok) {
      const errorText = await res.text();
      throw new ApiError(parseApiError(errorText, 'Не удалось удалить голосовой профиль'), res.status);
    }
  },

  async checkDbHealth(): Promise<{
    status: string;
    database: string;
    speakers_count?: number;
    meetings_count?: number;
    message?: string;
  }> {
    const res = await apiFetch(`${API_BASE_URL}/api/health/db`);
    return handleResponse(res, 'Ошибка проверки БД');
  },

  // ===== Отделы и приоритеты (задача P2) =====

  /** Список отделов доступен любому аутентифицированному пользователю на бэке. */
  async listDepartments(): Promise<DepartmentItem[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/departments`);
    return handleResponse<DepartmentItem[]>(res, 'Не удалось загрузить отделы');
  },

  async createDepartment(payload: DepartmentCreatePayload): Promise<DepartmentItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/departments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<DepartmentItem>(res, 'Не удалось создать отдел');
  },

  async updateDepartment(id: number, payload: DepartmentUpdatePayload): Promise<DepartmentItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/departments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<DepartmentItem>(res, 'Не удалось обновить отдел');
  },

  async deleteDepartment(id: number): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/api/departments/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const errorText = await res.text();
      throw new ApiError(parseApiError(errorText, 'Не удалось удалить отдел'), res.status);
    }
  },

  /** Полный список пользователей (admin-only) — для страницы «Отделы и приоритеты». */
  async listAdminUsers(): Promise<AdminUserItem[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/admin/users`);
    return handleResponse<AdminUserItem[]>(res, 'Не удалось загрузить пользователей');
  },

  async updateUserDepartment(userId: number, departmentId: number | null): Promise<AdminUserItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/admin/users/${userId}/department`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ department_id: departmentId }),
    });
    return handleResponse<AdminUserItem>(res, 'Не удалось назначить отдел');
  },

  /** priority=null сбрасывает личный override — приоритет снова наследуется от отдела. */
  async updateUserPriority(userId: number, priority: number | null): Promise<AdminUserItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/admin/users/${userId}/priority`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ priority }),
    });
    return handleResponse<AdminUserItem>(res, 'Не удалось задать приоритет');
  },

  // ===== Глоссарий терминов (задача GL1, admin-only) =====

  /** Список всех терминов (включая неактивные) — управляет им целиком только admin. */
  async listGlossary(): Promise<GlossaryTermItem[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/glossary`);
    return handleResponse<GlossaryTermItem[]>(res, 'Не удалось загрузить глоссарий');
  },

  async createGlossaryTerm(payload: GlossaryTermCreatePayload): Promise<GlossaryTermItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/glossary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<GlossaryTermItem>(res, 'Не удалось создать термин');
  },

  async updateGlossaryTerm(id: number, payload: GlossaryTermUpdatePayload): Promise<GlossaryTermItem> {
    const res = await apiFetch(`${API_BASE_URL}/api/glossary/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<GlossaryTermItem>(res, 'Не удалось обновить термин');
  },

  async deleteGlossaryTerm(id: number): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/api/glossary/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const errorText = await res.text();
      throw new ApiError(parseApiError(errorText, 'Не удалось удалить термин'), res.status);
    }
  },

  // ===== Бот-сессии — подключение бота к встрече (задача F2) =====

  /** 400 — неподдерживаемая платформа (текст ошибки бэкенда прокидывается в message). */
  async createBotSession(payload: BotSessionCreatePayload): Promise<BotSessionResponse> {
    const res = await apiFetch(`${API_BASE_URL}/api/bots/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleResponse<BotSessionResponse>(res, 'Не удалось подключить бота к встрече');
  },

  /** Мои сессии, новые первыми (admin/manager на бэкенде видят все — см. bots.py). */
  async listBotSessions(): Promise<BotSessionResponse[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/bots/sessions`);
    return handleResponse<BotSessionResponse[]>(res, 'Не удалось загрузить бот-сессии');
  },

  /** Для живого поллинга статуса одной сессии (bot-connect-modal). */
  async getBotSession(id: number): Promise<BotSessionResponse> {
    const res = await apiFetch(`${API_BASE_URL}/api/bots/sessions/${id}`);
    return handleResponse<BotSessionResponse>(res, 'Не удалось получить статус бота');
  },
};
